import { inject, Injectable, signal } from '@angular/core';
import { AssignmentType, AssignmentDto, PlanningService } from '../shared/generated/backend';
import { Observable, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export interface Conflict {
  AssignmentNo: number;
  isvalid: boolean;
  checkNeeded: boolean;
  reason?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PlanningHelperService {
  private _planningRef: Subject<{ AssignmentNo: number; Type: AssignmentType; Operation: string; msg?: string }> =
    new Subject<{
      AssignmentNo: number;
      Type: AssignmentType;
      Operation: string;
    }>(); // reference to the assignments in planning
  private _OmCheck: Subject<number> = new Subject(); // flag to the assignments in need for en OM check

  private planAssignment = new Subject<AssignmentDto[]>();
  public planAssignment$ = this.planAssignment.asObservable();

  // Backed by a signal so OnPush views reading it (e.g. to disable buttons) re-render when it changes.
  private readonly _planningInProgress = signal(false);
  public get planningInProgress(): boolean {
    return this._planningInProgress();
  }
  public set planningInProgress(value: boolean) {
    this._planningInProgress.set(value);
  }

  private translationService = inject(TranslateService);
  private planningService = inject(PlanningService);

  onPlanningFinished(): Observable<{ AssignmentNo: number; Type: AssignmentType; Operation: string }> {
    this._planningRef = new Subject<{ AssignmentNo: number; Type: AssignmentType; Operation: string }>();
    return this._planningRef.asObservable();
  }

  public planAssignments(assignments: AssignmentDto[]) {
    this.planAssignment.next(assignments);
  }

  // can be called from the component to signal that the current planning step is done
  async notifyPlanningStepDone(index: { AssignmentNo: number; Type: AssignmentType }, operation: 'done' | 'removed') {
    // console.log('planningFinished', index);
    if (index) {
      this._planningRef.next({ ...index, Operation: 'planning-' + operation });
    } else {
      this._planningRef.error(new Error('planningFinished: assignmentId is undefined'));
      throw new Error('planningFinished: assignmentId is undefined');
    }
  }

  public notifyPlanningComplete() {
    this._planningRef.complete();
  }

  // can be called to abort the planning
  abortPlanning(err: { index: { AssignmentNo: number; Type: AssignmentType }; msg: string }) {
    console.error('abortPlanning', err);
    this._planningRef.error({ ...err, Operation: 'planning-removed' });
  }

  /*
Anlieferung:
Datum der Tour vor «Dispo Datum Von» => Hinweis auf Prüfung durch Operations Management
Datum der Tour nach «VA[1] Datum Von» => Abbruch!
Abholung:
Datum der Tour vor «VA Datum Bis» => Hinweis auf Prüfung durch Operations Management
Datum der Tour nach «Dispo Datum Bis» => Hinweis auf Prüfung durch Operations Management
Ist eine Prüfung durch Operations Managment ausstehend, muss die Tour gekennzeichnet werden. Aktuell geschieht dies über einen roten Farbverlauf.
Über einen Button muss das Operations Mangement informiert werden können.
Dabei wir vom System ein generischer Eintrag in einem vordefinierten MS Teams Channel erstellt. Die Mitteilung enthält die ID der Tour.
*/

  private getDateWithoutTime(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  public async verifyTimeliness(
    project: { FromDateTime?: string; UntilDateTime?: string },
    assignment: { AssignmentNo: number; Type: AssignmentType; EventDate: string },
    dispoFrom: Date,
    dispoTo: Date
  ): Promise<Conflict> {
    const projectFrom = new Date(project.FromDateTime ?? '');
    const projectTo = new Date(project.UntilDateTime ?? '');
    const eventFrom = new Date(assignment.EventDate ?? '');

    switch (assignment.Type) {
      case AssignmentType.AssignmentDelivery: // Anlieferung
      case AssignmentType.PickupRentalPurchasingOrder:
        if (this.getDateWithoutTime(projectFrom) > eventFrom && eventFrom.getFullYear() > 1900) {
          // Datum der Tour nach «VA[1] Datum Von» => Abbruch!
          return {
            AssignmentNo: assignment.AssignmentNo,
            isvalid: false,
            checkNeeded: false,
            reason: this.translationService.instant('ASSIGNMENT.ERROR_VA'),
          };
        }

        //Datum der Tour vor «Dispo Datum Von» => Hinweis auf Prüfung durch Operations Management
        if (projectFrom < dispoFrom && dispoFrom.getFullYear() > 1900) {
          return {
            AssignmentNo: assignment.AssignmentNo,
            isvalid: true,
            checkNeeded: true,
            reason: 'MoveForward',
          };
        }

        return { AssignmentNo: assignment.AssignmentNo, isvalid: true, checkNeeded: false };
      case AssignmentType.AssignmentPickup: // Abholung
      case AssignmentType.ReturnRentalOrder:
        // Datum der Tour nach «Dispo Datum Bis» => Hinweis auf Prüfung durch Operations Management
        if (this.getDateWithoutTime(projectTo) > this.getDateWithoutTime(dispoTo) && dispoTo.getFullYear() > 1900) {
          // project starts after it should be picked up
          return {
            AssignmentNo: assignment.AssignmentNo,
            isvalid: true,
            checkNeeded: true,
            reason: 'Extend',
          };
        }
        return { AssignmentNo: assignment.AssignmentNo, isvalid: true, checkNeeded: false };
      default:
        console.error('Unknown AssignmentType', assignment);
        return {
          AssignmentNo: assignment.AssignmentNo,
          isvalid: false,
          checkNeeded: false,
          reason: 'Unbekannte Satzart',
        };
    }
  }

  requestOmCheck(projectNo: number) {
    // console.log('requestOmCheck', projectNo);
    this._OmCheck.next(projectNo);
  }

  onOpMManagementCheck() {
    return this._OmCheck;
  }
}
